import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mic, Users, Video, Calendar, Star, ChevronRight } from 'lucide-react';

export default function MotivationalSpeakingPage() {
  const services = [
    {
      icon: Mic,
      title: "Keynote Speaking",
      description: "Inspiring keynote presentations on VR education, innovation, and the future of learning for conferences and corporate events."
    },
    {
      icon: Users,
      title: "Workshop Facilitation",
      description: "Interactive workshops that engage participants in hands-on exploration of virtual reality and immersive learning."
    },
    {
      icon: Video,
      title: "Virtual Presentations",
      description: "Dynamic virtual presentations and webinars that bring the power of VR education to remote audiences worldwide."
    },
    {
      icon: Calendar,
      title: "Educational Consulting",
      description: "Strategic consulting for institutions looking to integrate VR and immersive technologies into their curriculum."
    },
  ];

  const topics = [
    "The Future of Education in the Metaverse",
    "Building Inclusive Learning Environments Through VR",
    "Cultural Preservation Through Virtual Reality",
    "Organizational Innovation in Educational Institutions",
    "Neurodiversity and Immersive Learning",
    "Global Connectivity in Virtual Classrooms"
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="motivational-speaking-page">
      {/* Hero Section */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-testid="speaking-title">
              Motivational Speaking
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Dr. Jason E. Williams inspires audiences worldwide with his vision for the future of education, combining expertise in organizational innovation with hands-on experience in virtual reality learning.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="book-speaking">
              <Calendar className="w-5 h-5" />
              Book Dr. Williams
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-serif font-bold text-white text-center mb-12"
          >
            Speaking Services
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-xl glass-light card-hover"
                data-testid={`service-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-muted-foreground">{service.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="py-16">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-serif font-bold text-white text-center mb-12"
          >
            Speaking Topics
          </motion.h2>

          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {topics.map((topic, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="flex items-center gap-3 p-4 rounded-lg glass-light"
                  data-testid={`topic-${index}`}
                >
                  <Star className="w-5 h-5 text-cta flex-shrink-0" />
                  <span className="text-white">{topic}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
              Ready to Inspire Your Audience?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Whether you're hosting a conference, corporate event, or educational workshop, Dr. Williams delivers presentations that transform perspectives and ignite innovation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Request Speaking Engagement
                <ChevronRight className="w-5 h-5" />
              </Link>
              <Link to="/media" className="btn-secondary inline-flex items-center gap-2">
                Watch Past Presentations
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
